/**
 * 我的: 账号信息、资料编辑(昵称)、邀请码、修改密码、退出登录.
 *
 * 头像显示使用内置预设(lib/avatar.ts) + 主题语义色, 不做文件上传;
 * 标签配色统一走 lib/badges.ts; 修改密码仅在登录态可用, 需要校验当前密码;
 * 邀请码文案只在剪贴板里出现(见 lib/invite.ts).
 */

import { ApiError, api } from '../lib/api'
import { memberBadgeClass } from '../lib/badges'
import { normalizeLanguage } from '../lib/content-language'
import { formatDate } from '../lib/format'
import { buildInviteText } from '../lib/invite'
import { t } from '../lib/i18n'
import { authRef } from '../stores/auth'
import { toast } from '../stores/toast'
import type { ContentLanguage, HighlightLevel, InviteRecord } from '../types/api'

/** 邀请记录的展示行: 日期在这里先格式化, 模板里不做转换 */
interface InviteRow {
  email: string
  registeredAt: string
}

export function profilePage() {
  return {
    editingProfile: false,
    editingPassword: false,
    confirmLogoutOpen: false,
    nickname: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',

    // 系统设置(高亮配置)弹层
    settingsOpen: false,
    settingsLoading: false,
    settingsSaving: false,
    settingsError: '',
    settingsTab: 'beginner',
    settingsLevels: [] as HighlightLevel[],
    settingsSelected: [] as string[],

    // 语言设置(与高亮配置同属系统设置)
    settingsLanguage: 'bilingual' as ContentLanguage,

    /** 内容语言选项: 文案跟随界面语言, 值仍是后端契约 */
    get settingsLanguages(): { value: ContentLanguage; label: string }[] {
      return [
        { value: 'english', label: t('language.english') },
        { value: 'chinese', label: t('language.chinese') },
        { value: 'bilingual', label: t('language.bilingual') },
      ]
    },

    // 邀请码弹层: 邀请码本体取当前用户, 邀请记录按需拉取一次
    inviteOpen: false,
    inviteLoading: false,
    inviteError: '',
    inviteRecords: [] as InviteRow[],
    inviteLoaded: false,

    get activeLevelTags(): string[] {
      return this.settingsLevels.find((level) => level.value === this.settingsTab)?.tags ?? []
    },

    isTagEnabled(tag: string): boolean {
      return this.settingsSelected.includes(tag)
    },

    async openSettings(): Promise<void> {
      this.settingsOpen = true
      this.settingsError = ''
      this.settingsLoading = true
      this.settingsLanguage = normalizeLanguage(authRef()?.user?.language)
      try {
        const options = await api.highlight.get()
        this.settingsLevels = options.levels
        this.settingsSelected = options.selected
        if (!this.settingsLevels.some((level) => level.value === this.settingsTab)) {
          this.settingsTab = this.settingsLevels[0]?.value ?? 'beginner'
        }
      } catch (error) {
        this.settingsError = error instanceof ApiError ? error.message : t('toast.highlightLoadFailed')
      } finally {
        this.settingsLoading = false
      }
    },

    closeSettings(): void {
      this.settingsOpen = false
    },

    /** 保存内容语言; 乐观更新, 失败回退 */
    async setLanguage(language: ContentLanguage): Promise<void> {
      if (this.settingsLanguage === language || this.settingsSaving) return
      const previous = this.settingsLanguage
      this.settingsLanguage = language
      const store = authRef()
      const ok = await store?.updateProfile({ language })
      if (ok) {
        toast(t('toast.languageUpdated'))
        return
      }
      this.settingsLanguage = previous
      toast(store?.error || t('toast.languageSaveFailed'))
      store?.clearError()
    },

    /** 点击词表即启用/停用; 乐观更新, 失败回滚 */
    async toggleHighlightTag(tag: string): Promise<void> {
      if (this.settingsSaving) return
      const snapshot = this.settingsSelected
      this.settingsSelected = snapshot.includes(tag)
        ? snapshot.filter((item) => item !== tag)
        : [...snapshot, tag]
      this.settingsSaving = true
      try {
        this.settingsSelected = await api.highlight.save(this.settingsSelected)
        window.dispatchEvent(new Event('highlight-settings-changed'))
      } catch (error) {
        this.settingsSelected = snapshot
        toast(error instanceof ApiError ? error.message : t('toast.saveFailed'))
      } finally {
        this.settingsSaving = false
      }
    },

    get passwordMismatch(): boolean {
      return this.confirmPassword.length > 0 && this.newPassword !== this.confirmPassword
    },

    get canSavePassword(): boolean {
      return (
        this.currentPassword.length > 0 &&
        this.newPassword.length >= 8 &&
        this.newPassword === this.confirmPassword
      )
    },

    /** 会员状态文案: 生效中 / 已失效 */
    get memberStateLabel(): string {
      return t(authRef()?.user?.is_member ? 'profile.memberActive' : 'profile.memberExpired')
    },

    /** 有效期至: 仅会员显示日期, 非会员留空 */
    get memberUntilLabel(): string {
      return authRef()?.memberUntilLabel ?? ''
    },

    get memberBadgeClass(): string {
      return memberBadgeClass(authRef()?.user?.is_member ?? false)
    },

    openProfileEditor(): void {
      const user = authRef()?.user
      this.nickname = user?.nickname ?? ''
      this.editingProfile = true
    },

    openPasswordEditor(): void {
      this.currentPassword = ''
      this.newPassword = ''
      this.confirmPassword = ''
      this.editingPassword = true
    },

    async saveProfile(): Promise<void> {
      const store = authRef()
      if (!store) return
      const ok = await store.updateProfile({ nickname: this.nickname })
      if (ok) {
        this.editingProfile = false
        toast(t('toast.nicknameUpdated'))
      }
    },

    async savePassword(): Promise<void> {
      const store = authRef()
      if (!store || !this.canSavePassword) return
      const ok = await store.changePassword(this.currentPassword, this.newPassword)
      if (ok) {
        this.editingPassword = false
        this.currentPassword = ''
        this.newPassword = ''
        this.confirmPassword = ''
        toast(t('toast.passwordUpdated'))
      }
    },

    /** 退出是破坏性操作, 桌面与手机一致先弹确认 (手机为底部卡片) */
    requestLogout(): void {
      this.confirmLogoutOpen = true
    },

    confirmLogout(): void {
      this.confirmLogoutOpen = false
      authRef()?.logout()
    },

    // --- 邀请码 -----------------------------------------------------------

    /** 邀请码来自当前用户信息, 不必等接口就能展示 */
    get inviteCode(): string {
      return authRef()?.user?.invite_code ?? ''
    },

    get inviteEmpty(): boolean {
      return !this.inviteLoading && this.inviteRecords.length === 0
    },

    /** 打开邀请弹层并拉取邀请记录(成功过一次就不再重复拉, 避免每次切换都闪) */
    async openInvite(): Promise<void> {
      this.inviteOpen = true
      this.inviteError = ''
      if (this.inviteLoaded || this.inviteLoading) return
      this.inviteLoading = true
      try {
        const summary = await api.users.invite()
        this.inviteRecords = summary.items.map((item: InviteRecord) => ({
          email: item.email,
          registeredAt: formatDate(item.registered_at),
        }))
        this.inviteLoaded = true
      } catch (error) {
        this.inviteError = error instanceof ApiError ? error.message : t('toast.inviteLoadFailed')
      } finally {
        this.inviteLoading = false
      }
    },

    closeInvite(): void {
      this.inviteOpen = false
    },

    /** 复制邀请文案(页面上不展示文案, 只展示邀请码) */
    async copyInvite(): Promise<void> {
      const code = this.inviteCode
      if (!code) {
        toast(t('toast.inviteNotReady'))
        return
      }
      try {
        await navigator.clipboard.writeText(buildInviteText(code, authRef()?.displayName ?? ''))
        toast(t('toast.inviteCopied'))
      } catch {
        toast(t('toast.copyFailed'))
      }
    },
  }
}
